import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { format, addDays, subDays, isSameDay, isTomorrow, startOfWeek, endOfWeek, isAfter, isBefore } from 'date-fns'
import clsx from 'clsx'
import pt from 'date-fns/locale/pt-BR'
import { Link } from 'react-router-dom'

import { CoreIcons } from 'components'
import {
  Typography,
  Card,
  Spinner,
  Avatar,
  Tabs,
  Tab,
  TabPanel,
  useDimensions
} from '@telavita-core/react-design-kit'

import * as routes from '../../settings/_routesSettings'
import Skeleton from 'components/Skeleton/Skeleton'
import { ReactComponent as PlayIcon } from '../../static/svg/play.svg'
import { ReactComponent as StarIcon } from '../../static/svg/star.svg'
import { QuickSchedule } from './QuickSchedule/QuickSchedule'
import { useQuickSchedule } from 'views/Homepage/context/QuickScheduleContext'
import { ScheduleInSameWeekFeedbackError } from './QuickSchedule/components/ScheduleInSameWeekFeedbackError/ScheduleInSameWeekFeedbackError'
import { usePatientPlanType } from 'hooks/usePatientPlanType'
import { ALLOWED_TO_SCHEDULE } from 'settings/_patientTreatmentStatus'
import { searchQueryAvailabilitiesDate } from 'utils/availabilitiesSearchHandler'

import { modalTypes } from 'containers/ModalManager/ModalManager'
import { showModal, hideModal } from 'redux/actions'
import { usePersonGeneralData } from 'hooks'

export const ProfessionalCardLoading = () => {
  const { isMobile } = useDimensions()

  return (
    <Card customClassName='ProfessionalCard ProfessionalCard__loading'>
      <div className='ProfessionalCard__sectionLoading__info'>
        <div className='ProfessionalCard__informationWrapperLoading'>
          <Skeleton
            type='avatar'
            width={92}
            borderRadius={50}
          />
          <div className='ProfessionalCard__informationWrapperLoading__data'>
            <Skeleton
              type='text'
              height={20}
              width={isMobile ? 182 : 225}
            />
            <Skeleton
              type='text'
              height={15}
              width={isMobile ? 158 : 180}
            />
            <div style={{ display: 'flex', gap: 20 }}>
              <Skeleton
                type='text'
                height={15}
                width={isMobile ? 130 : 108}
              />
              {!isMobile && (
                <Skeleton
                  type='text'
                  height={15}
                  width={108}
                />
              )}
            </div>
          </div>
        </div>
        <div className='ProfessionalCard__tabLoading'>
          <Skeleton
            type='text'
            height={15}
          />
          <Skeleton
            type='text'
            height={15}
          />
          <Skeleton
            type='text'
            height={15}
          />
        </div>
        <div className='ProfessionalCard__descriptionLoading'>
          <div className='ProfessionalCard__descriptionLoading__wrapper'>
            <Skeleton
              type='text'
              height={15}
            />
            <Skeleton
              type='text'
              height={15}
            />
            <Skeleton
              type='text'
              height={15}
            />
            <Skeleton
              type='text'
              height={15}
            />
            <Skeleton
              type='text'
              height={15}
              width={isMobile ? 118 : 250}
            />
          </div>
          <Skeleton
            type='text'
            height={15}
            width={139}
          />
        </div>
      </div>
      <div className='ProfessionalCard__sectionLoading'>
        <Skeleton
          type='text'
          height={15}
          width={isMobile ? 202 : 220}
        />
        <div className='ProfessionalCard__availabilitiesLoading'>
          {new Array(14).fill(0).map((_, index) => (
            <Skeleton
              key={index}
              type='text'
              height={35}
              minWidth={64}
              borderRadius={50}
            />
          ))}
        </div>
        <div className='ProfessionalCard__submitLoadingButton'>
          <Skeleton
            type='text'
            height={48}
          />
        </div>
      </div>
    </Card>
  )
}

const loadingStatuses = {
  IDLE: 'IDLE',
  FETCHING: 'FETCHING',
  FETCHED: 'FETCHED'
}

const ProfessionalCard = ({
  documentType,
  hasPrice,
  pictureUrl,
  professionalId,
  professionalUuid,
  professionalAppointmentId,
  autoReservationEnabled,
  professionalBio,
  professionalDoc,
  professionalName,
  professionalUrl,
  currentFetchDate,
  professionalSkills,
  selectedSkill,
  professionalEducations,
  professionalHasVideo,
  productDuration,
  productPrice,
  professionalType,
  onShowVideo,
  onRequestAvailabilities,
  reviews,
  onShowReviewsModal,
  patientAlreadyHasReservation,
  setPatientAlreadyHasReservation,
  patientInfo,
  ...rest
}) => {
  const dispatch = useDispatch()
  const firstAvailabilityDate = format(currentFetchDate, 'MM/dd/yyyy')
  const [availabilities, setAvailabilities] = useState({
    [firstAvailabilityDate]: []
  })
  const [selectedDay, setSelectedDay] = useState(firstAvailabilityDate)
  const [loadingAvailabilities, setLoadingAvailabilities] = useState(loadingStatuses.IDLE)
  const [activeTab, setActiveTab] = useState(selectedSkill?.codes?.length > 0 ? 1 : 0)
  const [patientNotAllowedToSchedulePsychology, setPatientNotAllowedToSchedulePsychology] = useState(false)
  const [patientSchedules, setPatientSchedules] = useState([])

  const {
    isCorporate,
    isEnterprise,
    isParticular,
    isCapitation
  } = usePatientPlanType()

  const {
    quickSchedule,
    setQuickSchedule,
    handleCreateOrder,
    isCreateOrderLoading,
    clearQuickSchedule
  } = useQuickSchedule()

  const isPatientAuthenticated = patientInfo?.isAuth

  const { hasAddressData, hasRelativeData } = usePersonGeneralData()

  const handleChange = (e, value) => {
    setActiveTab(value)
  }

  const handleSuccessCreateOrder = () => {
    dispatch(hideModal())
    handleCreateOrder()
  }

  const handleOpenAddressAndFamilyContactModal = () => {
    const modalData = {
      onSuccess: handleSuccessCreateOrder
    }
    dispatch(showModal(modalTypes.ADDRESS_AND_FAMILY_CONTACT, modalData))
  }

  function handleCreateNewSchedule() {
    const isPsychiatry = isPatientAuthenticated && professionalType === 'PSIQ'
    if (isPsychiatry) {
      const shouldShowAddressAndFamilyContactModal = !hasAddressData || !hasRelativeData
      const modalData = {
        onSuccess: () => {
          if (shouldShowAddressAndFamilyContactModal) {
            handleOpenAddressAndFamilyContactModal()
          } else {
            handleSuccessCreateOrder()
          }
        },
        onClose: () => { dispatch(hideModal()) }
      }
      return dispatch(showModal(modalTypes.PRESCRIPTION_DELIVERY_INFORMATION, modalData))
    }
    return  handleCreateOrder()
  }

  useEffect(() => {
    if (patientInfo) {
      const newSchedules = patientInfo?.patientSchedules.filter(newSchedule =>
        !patientSchedules.some(existingSchedule =>
          isSameDay(new Date(existingSchedule), new Date(newSchedule))
        )
      )

      if (newSchedules.length > 0) {
        setPatientSchedules(prevState => ([...prevState, ...newSchedules]))
      }
    }
  }, [patientInfo])

  const goToProfessionalProfile = (professionalUrl) => {
    if (professionalType === 'PSIQ') return routes.PROFESSIONAL_PSIQ_PROFILE(professionalUrl)

    return routes.PROFESSIONAL_PSIC_PROFILE(professionalUrl)
  }

  const onLoadAvailabilities = async (newDay, firstLoad) => {
    const newDayFormatted = format(newDay, 'MM/dd/yyyy')

    setSelectedDay(newDayFormatted)

    const isDayLoaded = Object.keys(availabilities).some(day => isSameDay(new Date(day), newDay))

    if (isDayLoaded && !firstLoad) return

    try {
      setLoadingAvailabilities(loadingStatuses.FETCHING)

      const { startQueryDateISO, endQueryDateISO } = searchQueryAvailabilitiesDate({
        selectedDay: new Date(newDay),
        currentFetchDate: new Date(currentFetchDate),
        isPsychologist: professionalType === 'PSIC'
      })

      const newAvailabilities = await onRequestAvailabilities({
        startDate: startQueryDateISO,
        endDate: endQueryDateISO,
      })

      const availabilities =  newAvailabilities.length > 0 ? newAvailabilities[0].options.map(availability => ({
        startDate: availability.startDate,
        endDate: availability.endDate,
        ...(availability.isRecurring !== undefined && { isRecurring: availability.isRecurring }),
      })) : []

      const startQueryDate = new Date(startQueryDateISO)
      const removedSlotsBeforeStartQueryDate = availabilities.filter(availability => isAfter(availability.startDate, startQueryDate))
      const isSomeAvailabilitiesInSelectedDay = availabilities.some(availability => isSameDay(availability.startDate, newDay))

      if (!isSomeAvailabilitiesInSelectedDay) {
        setAvailabilities(oldState => ({
          ...oldState,
          [newDayFormatted]: []
        }))
        return
      }

      setAvailabilities(oldState => ({
        ...oldState,
        [newDayFormatted]: removedSlotsBeforeStartQueryDate
      }))
    } catch (err) {
      const PATIENT_ALREADY_HAS_RESERVATION_STATUS_CODE = 403
      const PATIENT_NOT_ALLOWED_TO_SCHEDULE_STATUS_CODE = 404
      if (err === PATIENT_ALREADY_HAS_RESERVATION_STATUS_CODE) {
        setPatientAlreadyHasReservation(true)
      }

      if (err === PATIENT_NOT_ALLOWED_TO_SCHEDULE_STATUS_CODE) {
        setPatientNotAllowedToSchedulePsychology(true)
      }

      setAvailabilities(oldState => ({
        ...oldState,
        [newDayFormatted]: []
      }))
    } finally {
      setLoadingAvailabilities(loadingStatuses.FETCHED)
    }
  }

  useEffect(() => {
    onLoadAvailabilities(new Date(firstAvailabilityDate), true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  function handleSelectQuickSchedule(availabilities) {
    setQuickSchedule(prevState => ({
      ...prevState,
      professionalType,
      professionalUrl,
      professionalId,
      professionalUuid,
      professionalAppointmentId,
      autoReservationEnabled: Boolean(autoReservationEnabled),
      ...availabilities,
    }))
  }

  const isAvailabilitiesPropertyValid = Boolean(availabilities[selectedDay])
  const isAvailabilitiesEmpty = isAvailabilitiesPropertyValid && !availabilities[selectedDay].length
  const isAvailabilitiesFulfilled = isAvailabilitiesPropertyValid && availabilities[selectedDay].length > 0
  const isAvailabilitiesFetched = loadingAvailabilities === loadingStatuses.FETCHED
  const isFetchingAvailabilities = loadingAvailabilities === loadingStatuses.FETCHING

  const fulfilledAvailabilities = isAvailabilitiesFulfilled && availabilities[selectedDay]
  const fakeAvailabilities = Array.from({ length: 7 }, (_, index) => {
    const date = new Date()
    date.setHours(8 + index)
    date.setMinutes(0)
    return { startDate: date, endDate: date }
  })

  const isPatientEnterpriseOrCapitation = isEnterprise || isCapitation
  const isPatientSeeingPsychiatristsHomepage = professionalType === 'PSIQ'

  const patientNotAllowedToSchedulePsychiatry = isPatientSeeingPsychiatristsHomepage
    ? patientInfo?.psychiatryCycleStatus !== ALLOWED_TO_SCHEDULE
    : false

  const patientHasExpiredCredits = isPatientEnterpriseOrCapitation && !isPatientSeeingPsychiatristsHomepage
    ? isAfter(new Date(selectedDay), new Date(patientInfo?.creditsValidDate))
    : false

  const patientHasScheduleInSameDay = patientSchedules?.some(schedule =>
    isSameDay(new Date(selectedDay), new Date(schedule))
  )

  const patientHasScheduleInSameWeek = patientSchedules?.some(schedule => {
    const startOfSelectedDayWeek = startOfWeek(new Date(selectedDay))
    const endOfSelectedDayWeek = endOfWeek(new Date(selectedDay))
    const startOfScheduledWeek = startOfWeek(schedule)
    const endOfScheduleWeek = endOfWeek(schedule)
    return startOfScheduledWeek <= startOfSelectedDayWeek && endOfScheduleWeek >= endOfSelectedDayWeek
  })

  const showAlreadyHasScheduleFeedback = () => {
    /*
      Se o paciente é particular ou está vendo a vitrine de psiquiatria, a mensagem de feedback não é exibida.
    */
    if (isParticular || isPatientSeeingPsychiatristsHomepage) {
      return false
    }

    /*
      Se o paciente corporate já tem consulta na semana em que estiver
      pesquisando na vitrine, a mensagem de feedback é exibida.
    */
    if (isCorporate) {
      return patientHasScheduleInSameWeek
    }

    if (isPatientEnterpriseOrCapitation) {

      /*
        Se o paciente tem seus créditos expirados, a mensagem de feedback não é necessária
        pois o sistema já vai lidar com isso renderizando horários falsos (fakeAvailabilities).
      */
      if (patientHasExpiredCredits) {
        return false
      }

      /*
        Se o paciente já tem consulta no dia em que estiver pesquisando
        na vitrine a mensagem de feedback é exibida.
      */
      return patientHasScheduleInSameDay
    }
  }

  const shouldRenderFakeAvailabilities = () => {
    const reasonsToRenderFakeAvailabilities = [
      patientAlreadyHasReservation,
      patientHasExpiredCredits,
      patientNotAllowedToSchedulePsychology,
      patientNotAllowedToSchedulePsychiatry
    ]

    return reasonsToRenderFakeAvailabilities.some(reason => reason)
  }

  const shouldRenderAlreadyHasScheduleFeedback =
    isAvailabilitiesFetched &&
    !patientAlreadyHasReservation &&
    showAlreadyHasScheduleFeedback()

  const renderQuickSchedule = (availabilities = []) => {
    const shouldDisableScheduleButton = quickSchedule?.professionalUrl !== professionalUrl

    return (
      <QuickSchedule
        availabilities={availabilities}
        selectedDay={selectedDay}
        setSelectedSchedule={handleSelectQuickSchedule}
        selectedSchedule={quickSchedule}
        disableButton={shouldDisableScheduleButton}
        onClickToSchedule={handleCreateNewSchedule}
        loadingSubmit={isCreateOrderLoading}
        isAvailabilitiesEmpty={isAvailabilitiesEmpty}
      />
    )
  }

  const renderEmptyState = () => (
    <div className='ProfessionalCard__availabilities__status'>
      <Typography variant='content2' italic>Sem horários para este dia</Typography>
    </div>
  )

  const renderScheduleContent = () => {
    if (!isAvailabilitiesFetched) return

    if (isPatientAuthenticated && !isParticular && shouldRenderFakeAvailabilities()) {
      return renderQuickSchedule(fakeAvailabilities)
    }

    if (shouldRenderAlreadyHasScheduleFeedback) {
      return null
    }

    if (isAvailabilitiesEmpty) {
      return renderEmptyState()
    }

    return renderQuickSchedule(fulfilledAvailabilities)
  }

  const renderPriceAndDuration = () => {
    const isPsychiatrist = professionalType === 'PSIQ'
    const hasSeparator = hasPrice && productDuration && !isPsychiatrist
    
    return (
      <div className='ProfessionalCard__information__priceDurationContainer'>
        {hasPrice && (
          <Typography weight='bold' variant='content1'>
            R$ {Number(productPrice).toFixed()}
          </Typography>
        )}

        {hasSeparator && <strong>&nbsp;/&nbsp;</strong>}

        {!isPsychiatrist && (
          <Typography variant='content2'>
            {productDuration} min.
          </Typography>
        )}
      </div>
    )
  }

  return (
    <Card customClassName='ProfessionalCard' {...rest}>
      <div className='ProfessionalCard__wrapper'>
        <div className='ProfessionalCard__information'>
          <div className='ProfessionalCard__information__wrapper'>
            <Avatar
              src={pictureUrl}
              alt={professionalName}
              title={professionalName}
              inlineStyles={{
                width: 92,
                height: 92,
                minWidth: 92,
              }}
            />
            <div className='ProfessionalCard__information__content'>
              <Typography customClassName='ProfessionalCard__information__name' weight='bold' variant='header3'>
                {professionalName}
              </Typography>

              <Typography customClassName='ProfessionalCard__information__doc' variant='content2'>
                {professionalType === 'PSIQ' ? 'Médico(a) - ' : 'Psicólogo(a) - '} {documentType}{' '}
                {professionalDoc}
              </Typography>

              <div className='ProfessionalCard__information__priceWrapper'>
                {renderPriceAndDuration()}

                {professionalHasVideo && (
                  <button
                    className='ProfessionalCard__information__playvideo'
                    onClick={onShowVideo}
                  >
                    <PlayIcon />
                    <Typography weight='bolder' variant='content2' customClassName='ProfessionalCard__information__playvideo__text'>
                        Vídeo
                    </Typography>
                  </button>
                )}
                {reviews > 0 && (
                  <button
                    className='ProfessionalCard__information__reviews'
                    onClick={onShowReviewsModal}
                  >
                    <StarIcon />
                    <Typography weight='bolder' variant='content2' customClassName='ProfessionalCard__information__reviews__text'>
                        Avaliações
                    </Typography>
                    <Typography variant='content2'>
                      {`(${reviews})`}
                    </Typography>
                  </button>
                )}
              </div>

            </div>
          </div>
          <div className='ProfessionalCard__information__tabContainer'>
            <div className='ProfessionalCard__information__tabContainer__labelContainer'>
              <Tabs selectedTab={activeTab} onChange={handleChange}>
                <Tab label="Sobre" id={0} />
                <Tab label="Experiências" id={1} />
                <Tab label="Formação" id={2} />
              </Tabs>
            </div>
            <div className='ProfessionalCard__information__tabContainer__panelContainer'>
              <TabPanel isActive={activeTab === 0}>
                <Typography className='ProfessionalCard__information__tabContainer__bio'>
                  {professionalBio}
                </Typography>
              </TabPanel>
              <TabPanel isActive={activeTab === 1}>
                <div className='ProfessionalCard__information__skillsContainer'>
                  {professionalSkills.map((skill, index) => (
                    <p
                      key={skill.name + index}
                      className={clsx({
                        'ProfessionalCard__information__skillsContainer__item': true,
                        'ProfessionalCard__information__skillsContainer__item--active': selectedSkill && selectedSkill.name === skill.name,
                      })}
                    >
                      {skill.name}
                    </p>
                  ))}
                </div>
              </TabPanel>
              <TabPanel isActive={activeTab === 2}>
                <ul className='ProfessionalCard__information__educationsContainer'>
                  {professionalEducations.map((education, index) => (
                    <li className='ProfessionalCard__information__educationsContainer__item' key={index}>
                      <strong>{education.institution}{' '}</strong>
                      <span>{education.course_name} - {education.start_year}/{education.end_year}</span>
                    </li>
                  ))}
                </ul>
              </TabPanel>
            </div>
          </div>
          <Link
            to={() => goToProfessionalProfile(professionalUrl)}
            className='ProfessionalCard__information__button'
          >
                Ver perfil completo
          </Link>
        </div>
        {shouldRenderAlreadyHasScheduleFeedback
          ? <ScheduleInSameWeekFeedbackError />
          : (
            <div className='ProfessionalCard__availabilities'>
              <div className='ProfessionalCard__availabilities__controlsContainer'>
                <div className='ProfessionalCard__availabilities__controlsContainer__selectedDayContainer'>
                  <Typography customClassName='ProfessionalCard__availabilities__controlsContainer__selectedDayContainer__selectedDay' variant='content2'>
                  Horários em&nbsp;
                  </Typography>
                  <div className='ProfessionalCard__availabilities__controlsContainer__selectedDayContainer__wrapper'>
                    <Typography
                      customClassName='ProfessionalCard__availabilities__controlsContainer__selectedDayContainer__selectedShortDay'
                      weight='bolder'>
                      {format(
                        new Date(selectedDay),
                        'dd/MMM, ',
                        { locale: pt}
                      )}
                    </Typography>
                    <Typography weight='bolder' customClassName='ProfessionalCard__availabilities__controlsContainer__selectedDayContainer__selectedLongDay'>
                  &nbsp;
                      {isTomorrow(new Date(selectedDay)) ? 'amanhã' : (
                        format(
                          new Date(selectedDay),
                          'EEEEEE',
                          { locale: pt}
                        )
                      )}
                    </Typography>
                  </div>

                </div>
                <div className='ProfessionalCard__availabilities__controlsContainer__controls'>
                  <button
                    onClick={() => {
                      clearQuickSchedule()
                      onLoadAvailabilities(subDays(new Date(selectedDay), 1))
                    }}
                    disabled={isFetchingAvailabilities || isBefore(new Date(selectedDay), new Date())}
                  >
                    <CoreIcons
                      fill='#000'
                      name='ArrowLeft'
                      height='24px'
                      width='24px'
                    />
                  </button>
                  <button
                    onClick={() => {
                      clearQuickSchedule()
                      onLoadAvailabilities(addDays(new Date(selectedDay), 1))
                    }}
                    disabled={isFetchingAvailabilities}
                  >
                    <CoreIcons
                      fill='#000'
                      name='ArrowRight'
                      height='24px'
                      width='24px'
                    />
                  </button>
                </div>
              </div>

              {isFetchingAvailabilities && (
                <div className='ProfessionalCard__availabilities__status'>
                  <Spinner inlineStyles={{ height: 30, width: 30 }}/>
                </div>
              )}

              {renderScheduleContent()}

            </div>
          )
        }
      </div>

      <Link
        to={() => goToProfessionalProfile(professionalUrl)}
        className='ProfessionalCard__button'
      >
          Ver perfil completo
      </Link>
    </Card>
  )
}

export default ProfessionalCard
